import { Check, ChevronRight, Settings } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useDevices, type Denoise } from '../state/devices';
import { sendCallDiagnostics } from '../lib/call-diagnostics';
import { micLevel, useVoice } from '../state/voice';
import { canPickSpeaker, useDeviceList, useSettings } from './SettingsDialog';

const denoiseLabels: Record<Denoise, string> = {
  strong: 'Güçlü',
  experimental: 'Deneysel',
  browser: 'Tarayıcının kendi',
  off: 'Kapalı',
};

/**
 * Opens above its button; on phones (and in the narrow pop-out window) it spans the bottom
 * instead. Closes on Escape or a click elsewhere, in whichever window it lives.
 */
function Popover({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const doc = ref.current?.ownerDocument ?? document;
    const down = (event: PointerEvent) => {
      const target = event.target as Element;
      if (!ref.current?.contains(target) && !target.closest?.('[data-menu-trigger]')) close.current();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close.current();
    };
    doc.addEventListener('pointerdown', down);
    doc.addEventListener('keydown', key);
    return () => {
      doc.removeEventListener('pointerdown', down);
      doc.removeEventListener('keydown', key);
    };
  }, []);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      className="absolute bottom-[calc(100%+10px)] left-0 z-40 max-h-[70dvh] w-[300px] animate-enter overflow-y-auto rounded-lg border border-line-strong bg-surface-2 p-1.5 shadow-2xl max-md:fixed max-md:inset-x-3 max-md:bottom-[calc(84px+env(safe-area-inset-bottom))] max-md:w-auto"
    >
      {children}
    </div>
  );
}

function Choices<T extends string>({ options, value, onChange }: { options: { id: T; label: string }[]; value: T; onChange: (id: T) => void }) {
  return (
    <div role="group" className="flex flex-col py-0.5">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          role="menuitemradio"
          aria-checked={option.id === value}
          onClick={() => onChange(option.id)}
          className="flex items-center gap-2 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left text-[13.5px] text-fg-1 hover:bg-surface-3"
        >
          <span className="min-w-0 flex-1 truncate">{option.label}</span>
          {option.id === value && <Check size={15} className="flex-none text-green-400" aria-hidden="true" />}
        </button>
      ))}
    </div>
  );
}

/** A setting row showing its current value; opens its choices in place. */
function Expandable({ title, value, open, onToggle, children }: { title: string; value: string; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-center gap-2 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left hover:bg-surface-3"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-semibold text-fg-1">{title}</span>
          <span className="block truncate text-xs text-fg-3">{value}</span>
        </span>
        <ChevronRight size={16} className={`flex-none text-fg-3 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden="true" />
      </button>
      {open && <div className="ml-2 border-l border-line pl-1">{children}</div>}
    </div>
  );
}

function useDeviceOptions(kind: MediaDeviceKind, fallback: string) {
  const { devices } = useDeviceList();
  const list = devices.filter((d) => d.kind === kind && d.deviceId && d.deviceId !== 'default');
  return [{ id: 'default', label: 'Sistem varsayılanı' }, ...list.map((d, i) => ({ id: d.deviceId, label: d.label || `${fallback} ${i + 1}` }))];
}

const nameOf = (options: { id: string; label: string }[], id: string) => options.find((o) => o.id === id)?.label ?? 'Sistem varsayılanı';

/** The own mic's level from the call itself (no second capture), redrawn in its own window. */
function LiveMeter() {
  const bar = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const view = bar.current?.ownerDocument.defaultView ?? window;
    let frame = 0;
    const tick = () => {
      if (bar.current) bar.current.style.width = `${Math.round(micLevel() * 100)}%`;
      frame = view.requestAnimationFrame(tick);
    };
    tick();
    return () => view.cancelAnimationFrame(frame);
  }, []);
  return (
    <div className="h-2 overflow-hidden rounded-full bg-surface-3" role="img" aria-label="Mikrofon seviyesi">
      <span ref={bar} className="block h-full w-0 rounded-full bg-green-400 transition-[width] duration-75" />
    </div>
  );
}

const divider = <hr className="my-1.5 border-0 border-t border-line" />;

function SettingsLink({ label, onClose }: { label: string; onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={() => {
        onClose();
        useSettings.getState().show();
      }}
      className="flex w-full items-center gap-2.5 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left text-[13.5px] font-semibold text-fg-1 hover:bg-surface-3"
    >
      <Settings size={16} className="text-fg-2" aria-hidden="true" />
      {label}
    </button>
  );
}

export function AudioMenu({ onClose }: { onClose: () => void }) {
  const prefs = useDevices();
  const micOn = useVoice((s) => s.micOn);
  const deafened = useVoice((s) => s.deafened);
  const outputVolume = useVoice((s) => s.outputVolume);
  const mics = useDeviceOptions('audioinput', 'Mikrofon');
  const speakers = useDeviceOptions('audiooutput', 'Hoparlör');
  const [open, setOpen] = useState<'mic' | 'denoise' | 'speaker' | null>(null);
  const toggle = (section: typeof open) => setOpen((current) => (current === section ? null : section));

  return (
    <Popover label="Ses seçenekleri" onClose={onClose}>
      <Expandable title="Mikrofon" value={nameOf(mics, prefs.micId)} open={open === 'mic'} onToggle={() => toggle('mic')}>
        <Choices options={mics} value={prefs.micId} onChange={(micId) => prefs.set({ micId })} />
      </Expandable>
      <Expandable title="Gürültü engelleme" value={denoiseLabels[prefs.denoise]} open={open === 'denoise'} onToggle={() => toggle('denoise')}>
        <Choices
          options={(Object.keys(denoiseLabels) as Denoise[]).map((id) => ({ id, label: denoiseLabels[id] }))}
          value={prefs.denoise}
          onChange={(denoise) => prefs.set({ denoise })}
        />
      </Expandable>
      {canPickSpeaker && (
        <Expandable title="Hoparlör" value={nameOf(speakers, prefs.speakerId)} open={open === 'speaker'} onToggle={() => toggle('speaker')}>
          <Choices options={speakers} value={prefs.speakerId} onChange={(speakerId) => prefs.set({ speakerId })} />
        </Expandable>
      )}
      {divider}
      <div className="flex flex-col gap-2 px-2.5 py-2">
        <span className="text-[13.5px] font-semibold">Mikrofon seviyesi</span>
        {micOn ? <LiveMeter /> : <span className="text-xs text-fg-3">Mikrofonun kapalı.</span>}
      </div>
      <label className="flex flex-col gap-2 px-2.5 py-2">
        <span className="flex items-baseline justify-between text-[13.5px] font-semibold">
          Herkesin sesi
          <span className="text-xs font-normal text-fg-3">%{Math.round(outputVolume * 100)}</span>
        </span>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(outputVolume * 100)}
          onChange={(e) => useVoice.getState().setOutputVolume(Number(e.target.value) / 100)}
          className="w-full accent-brand-500"
        />
      </label>
      {divider}
      <label className="flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-2 text-[13.5px] font-semibold hover:bg-surface-3">
        <span className="flex-1">Sesi kapat (kimseyi duyma)</span>
        <input type="checkbox" checked={deafened} onChange={() => useVoice.getState().toggleDeafen()} className="size-4 accent-brand-500" />
      </label>
      <DiagnosticsButton />
      <SettingsLink label="Ses ayarları" onClose={onClose} />
    </Popover>
  );
}

/** Sends the call's last two minutes to us, right after a strange sound (see lib/call-diagnostics.ts). */
function DiagnosticsButton() {
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const send = async () => {
    setState('sending');
    setState((await sendCallDiagnostics('menu')) ? 'sent' : 'failed');
  };
  return (
    <button
      type="button"
      disabled={state === 'sending' || state === 'sent'}
      onClick={() => void send()}
      className="flex w-full flex-col items-start gap-0.5 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left hover:bg-surface-3 disabled:opacity-70"
    >
      <span className="text-[13.5px] font-semibold">{state === 'sent' ? 'Teşhis kaydı gönderildi' : state === 'failed' ? 'Gönderilemedi, tekrar dene' : 'Teşhis kaydını gönder'}</span>
      <span className="text-xs text-fg-3">Garip bir ses duyduysan hemen bas: son 2 dakikanın ses seviyeleri gider, konuşmalar gitmez.</span>
    </button>
  );
}

export function VideoMenu({ onClose }: { onClose: () => void }) {
  const prefs = useDevices();
  const cameras = useDeviceOptions('videoinput', 'Kamera');
  return (
    <Popover label="Kamera seçenekleri" onClose={onClose}>
      <p className="m-0 px-2.5 pt-1.5 pb-0.5 text-xs font-semibold text-fg-3">Kamera</p>
      <Choices options={cameras} value={prefs.cameraId} onChange={(cameraId) => prefs.set({ cameraId })} />
      {divider}
      <SettingsLink label="Görüntü ayarları" onClose={onClose} />
    </Popover>
  );
}
